import React, { type FunctionComponent, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form-7'
import { useHistory } from 'react-router-dom'
import {
  spacing,
  Section,
  Button,
  Text,
  colors,
  CenteredContent,
} from '@retailer-platform/shared-components'
import styled from '@emotion/styled'
import { LoadingGenericBase } from '@instacart/ids-core'
import _ from 'lodash'
import { RetailerCollectionsFormDetails } from 'domains/retailer-collections/components/form/components/details/RetailerCollectionsFormDetails'
import { RetailerCollectionsFormAssets } from 'domains/retailer-collections/components/form/components/assets/RetailerCollectionsFormAssets'
import {
  type CollectionType,
  type RetailerCollectionFormMode,
  type RetailerCollectionFormValues,
} from 'domains/retailer-collections/components/form/utils/types'
import {
  RetailerCollectionSurface,
  useGetRetailerCollectionQuery,
} from 'domains/retailer-collections/api'
import { useRetailerId } from 'utils/routing/params.hooks'
import { ErrorPage } from 'gin-and-tonic/containers/error-page/ErrorPage'
import { RetailerCollectionsFormFooterPortal } from 'domains/retailer-collections/components/form/components/footer/RetailerCollectionsFormFooter'
import { useDomainMessages } from 'domains/retailer-collections/utils/domain/intl'
import { useWarehouseContext } from 'utils/contexts/warehouse/WarehouseContext.hooks'
import {
  RetailerCollectionStatus,
  RetailerCollectionContentType,
} from 'domains/retailer-collections/utils/types'
import { addTranslationShims } from 'domains/retailer-collections/components/form/utils/utils'
import { MARKETPLACE_STORE_CONFIG_ID } from 'domains/retailer-collections/utils/constants'

interface RetailerCollectionsFormProps {
  passedFormState?: Partial<RetailerCollectionFormValues>
  onSubmit: (formState: RetailerCollectionFormValues) => void
  loading?: boolean
  mode: RetailerCollectionFormMode
  collectionType: CollectionType
}

const Loading = () => (
  <div
    css={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(255,255,255,0.7)',
    }}
  >
    <LoadingGenericBase />
  </div>
)

const randomString = () => _.times(5, () => _.random(35).toString(36)).join('')

export const defaultFormState: RetailerCollectionFormValues = {
  parentId: null,
  name: '',
  slug: '',
  translations: [],
  surfaces: [RetailerCollectionSurface.Marketplace],
  contentType: RetailerCollectionContentType.Collections,
  status: RetailerCollectionStatus.Active,
  tileImageUrl: null,
  effectiveDates: {
    startDate: null,
    endDate: null,
  },
  position: null,
  storeConfigurations: [
    {
      id: MARKETPLACE_STORE_CONFIG_ID,
      slug: 'marketplace',
      surface: RetailerCollectionSurface.Marketplace,
      url: 'https://www.instacart.com',
      name: 'Marketplace',
      pinned: false,
    },
  ],
}

const SectionContent = styled.div({
  padding: spacing.X24,
  display: 'flex',
  flexDirection: 'column',
})

export const RetailerCollectionsForm: FunctionComponent<RetailerCollectionsFormProps> = ({
  passedFormState = {},
  onSubmit,
  loading,
  mode,
  collectionType,
}) => {
  const { initialFormState } = React.useMemo(
    () => ({ initialFormState: { ...defaultFormState, ...passedFormState } }),
    [passedFormState]
  )

  const { warehouse } = useWarehouseContext()
  const retailerId = useRetailerId()
  const history = useHistory()
  const messages = useDomainMessages({
    detailsTitle: 'retailerCollectionsDomain.form.section.details',
    assetsTitle: 'retailerCollectionsDomain.form.section.assets',
    processingTime: 'retailerCollectionsDomain.form.processingTime',
    save: 'retailerCollectionsDomain.form.actions.save',
    cancel: 'retailerCollectionsDomain.form.actions.cancel',
    copy: 'retailerCollectionsDomain.form.actions.copy',
  })

  // This takes care of ensuring all supported locales exist within the form
  const expandedInitialFormState = useMemo(() => {
    const expandedForm = {
      ...initialFormState,
      translations: addTranslationShims(warehouse.locales, initialFormState.translations),
    }

    if (mode == 'duplicate') {
      expandedForm.name += ` - ${messages.copy}`
      expandedForm.slug += `-${randomString()}-${messages.copy}`
    }

    return expandedForm
  }, [initialFormState, warehouse.locales, mode, messages.copy])

  const form = useForm<RetailerCollectionFormValues>({
    defaultValues: expandedInitialFormState,
  })

  const currentParentId = form.watch('parentId')

  const {
    data: parentCollectionData,
    loading: parentCollectionLoading,
    error: parentCollectionError,
    refetch,
  } = useGetRetailerCollectionQuery({
    variables: {
      id: currentParentId,
      retailerId,
    },
    skip: !currentParentId || collectionType == 'instacart',
  })

  const isLoading = parentCollectionLoading || loading

  const parentCollectionLevel =
    parentCollectionData?.retailerCollectionsServiceGetRetailerCollection?.hierarchy.length - 1 ?? 0

  const parentCollection =
    parentCollectionData?.retailerCollectionsServiceGetRetailerCollection?.retailerCollection

  if (parentCollection) {
    // use parent's store config if there is a parent
    const storeConfigurations = parentCollection.storeConfigurations.map(s => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { __typename, ...rest } = s
      return rest
    })
    form.setValue('storeConfigurations', storeConfigurations)
  }

  if (parentCollectionError)
    return (
      <ErrorPage
        action={() => {
          refetch()
        }}
      />
    )

  return (
    <CenteredContent>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} css={{ display: 'contents' }}>
          <Section title={messages.detailsTitle}>
            <SectionContent>
              <RetailerCollectionsFormDetails
                mode={mode}
                collectionType={collectionType}
                parentRetailerCollection={parentCollection}
                parentRetailerCollectionLevel={parentCollectionLevel}
              />
            </SectionContent>
          </Section>

          {collectionType === 'retailer_collection' && (
            <Section title={messages.assetsTitle}>
              <SectionContent>
                <RetailerCollectionsFormAssets />
              </SectionContent>
            </Section>
          )}

          <RetailerCollectionsFormFooterPortal>
            <Text css={{ color: colors.GRAYSCALE.X50 }} size="medium">
              {messages.processingTime}
            </Text>

            <Button onClick={() => history.goBack()} disabled={isLoading} variant="secondary">
              {messages.cancel}
            </Button>

            <Button onClick={form.handleSubmit(onSubmit)} disabled={isLoading}>
              {messages.save}
            </Button>
          </RetailerCollectionsFormFooterPortal>
        </form>
      </FormProvider>

      {isLoading && <Loading />}
    </CenteredContent>
  )
}
